import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from '@mui/material/utils';
import { useTranslation } from 'react-i18next';

import { Autocomplete, AutocompletePlaceOption } from '../../Autocomplete';

export type GoogleMapAutocompleteValue = {
  lng: number;
  lat: number;
};

export type Option = {
  label: string;
  placeId: string;
  description: string;
  secondaryText: string;
};

type GoogleMapAutocompleteProps = {
  onChange?: (value: GoogleMapAutocompleteValue | null) => void;
};

export const GoogleMapAutocomplete = ({ onChange }: GoogleMapAutocompleteProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<Option | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const autocompleteService = useRef<typeof google.maps.places.AutocompleteSuggestion>();
  const placesService = useRef<typeof google.maps.places.Place>();

  const debouncePlacePredictions = useCallback(
    (inputValue: string) =>
      debounce(() => {
        if (autocompleteService.current) {
          autocompleteService.current.fetchAutocompleteSuggestions({ input: inputValue }).then((result) => {
            const newOptions: Option[] = [];
            result.suggestions.forEach((item) => {
              newOptions.push({
                label: item.placePrediction?.mainText?.text ?? '',
                placeId: item.placePrediction?.placeId ?? '',
                secondaryText: item.placePrediction?.secondaryText?.text ?? '',
                description: item.placePrediction?.text.text ?? '',
              });
            });
            setOptions(newOptions);
          });
        }
      }, 400),
    [],
  );

  const handleChange = async (option: Option | null) => {
    if (option && placesService.current) {
      const Place = placesService.current;
      const currentPlace = new Place({ id: option.placeId });
      const result = await currentPlace.fetchFields({ fields: ['location'] });
      const { location } = result.place;

      if (location) {
        onChange?.({
          lng: location.lng(),
          lat: location.lat(),
        });
      }
    }
  };

  const initMap = async () => {
    const { Place, AutocompleteSuggestion } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;

    placesService.current = Place;
    autocompleteService.current = AutocompleteSuggestion;
  };

  useEffect(() => {
    // eslint-disable-next-line no-console
    if (!google) return console.error('Google has not been found.');

    initMap();

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }, []);

  return (
    <Autocomplete
      value={value}
      options={options}
      noOptionsText={t('search-location')}
      includeInputInList
      getOptionLabel={(option) => option.description}
      isOptionEqualToValue={(o, v) => o.description === v.description}
      size='small'
      sx={{ width: 240 }}
      onChange={(_, option) => {
        handleChange(option);
        setValue(option);
      }}
      onInputChange={(_, newInputValue) => {
        debouncePlacePredictions(newInputValue)();
      }}
      renderOption={(props, option) => (
        <AutocompletePlaceOption key={option.description} props={props} option={option} />
      )}
    />
  );
};
